
import { isNative } from "@/lib/capacitor";

export interface SpeechInputConfig {
  language: string;
  onInterim?: (text: string) => void;
  onFinal: (text: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

export interface SpeechInputProvider {
  readonly supportsInterim: boolean;
  isAvailable(): Promise<boolean>;
  ensurePermissions(): Promise<void>;
  startListening(config: SpeechInputConfig): void;
  stopListening(): void;
  dispose(): void;
}

// --- Web Speech API provider (desktop browsers) ---

export class WebSpeechInputProvider implements SpeechInputProvider {
  readonly supportsInterim = true;
  private recognition: any = null;

  async isAvailable(): Promise<boolean> {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    return !!SR;
  }

  async ensurePermissions(): Promise<void> {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (result.state === 'denied') {
          throw new Error('Microphone permission denied');
        }
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Microphone permission denied') throw err;
    }
  }

  startListening(config: SpeechInputConfig): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) throw new Error('SpeechRecognition not available');

    this.stopListening();
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = config.language;

    recognition.onstart = () => config.onStart?.();

    recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      // Start from event.resultIndex — with continuous=true, event.results
      // accumulates all results. Starting from 0 re-processes old results.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }
      if (interimText) config.onInterim?.(interimText);
      if (finalText) config.onFinal(finalText);
    };

    recognition.onerror = (e: any) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        config.onError?.(new Error(`Speech recognition error: ${e.error}`));
      }
    };

    recognition.onend = () => config.onEnd?.();

    this.recognition = recognition;
    try {
      recognition.start();
    } catch (_) { /* ignore duplicate start */ }
  }

  stopListening(): void {
    try {
      this.recognition?.stop();
    } catch (_) { /* ignore */ }
    this.recognition = null;
  }

  dispose(): void {
    this.stopListening();
  }
}

// --- Capacitor native provider (iOS/Android) ---

export class CapacitorSpeechInputProvider implements SpeechInputProvider {
  readonly supportsInterim = false;
  private listening = false;
  private plugin: any = null;

  private async getPlugin() {
    if (!this.plugin) {
      try {
        // Dynamic import — module installed by Task 8 (@capacitor-community/speech-recognition).
        // Use a variable to prevent Rollup/Vite from statically resolving this import
        // since the package may not be installed yet (web-only builds).
        const pkgName = '@capacitor-community/speech-recognition';
        const mod = await (Function('p', 'return import(p)') as (p: string) => Promise<any>)(pkgName);
        this.plugin = mod.SpeechRecognition;
      } catch {
        throw new Error('@capacitor-community/speech-recognition not installed');
      }
    }
    return this.plugin;
  }

  async isAvailable(): Promise<boolean> {
    if (!isNative()) return false;
    try {
      const plugin = await this.getPlugin();
      const result = await plugin.available();
      return result.available;
    } catch {
      return false;
    }
  }

  async ensurePermissions(): Promise<void> {
    const plugin = await this.getPlugin();
    const perms = await plugin.checkPermissions();
    if (perms.speechRecognition !== 'granted') {
      const requested = await plugin.requestPermissions();
      if (requested.speechRecognition !== 'granted') {
        throw new Error('Speech recognition permission denied');
      }
    }
  }

  startListening(config: SpeechInputConfig): void {
    this.listening = true;
    config.onStart?.();

    this.getPlugin().then(async (plugin: any) => {
      try {
        await plugin.addListener('partialResults', (data: any) => {
          if (!this.listening) return;
          const text = data.matches?.[0] || data.value || '';
          if (text) config.onFinal(text);
        });

        await plugin.start({
          language: config.language,
          maxResults: 1,
          partialResults: false,
          popup: false,
        });
      } catch (err) {
        config.onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  stopListening(): void {
    this.listening = false;
    this.getPlugin().then(async (plugin: any) => {
      try {
        await plugin.stop();
        await plugin.removeAllListeners();
      } catch (_) { /* ignore */ }
    });
  }

  dispose(): void {
    this.stopListening();
  }
}

// --- Factory ---

export function createSpeechInputProvider(): SpeechInputProvider {
  if (isNative()) {
    return new CapacitorSpeechInputProvider();
  }
  return new WebSpeechInputProvider();
}
